/**
 * cisPayrollColumns.ts — Shared CIS payroll column definitions.
 *
 * Architecture: Individual column functions + group convenience functions.
 * Same pattern as payeUmbrellaPayrollColumns.ts.
 *
 * CIS payrolls are invoice-focused (materials, VAT, gross, CIS deduction)
 * unlike PAYE/Umbrella which are employment-focused (NI, pension, SSP).
 *
 * Used by 5 pages:
 *   1. Draft Payroll (CIS) report
 *   2. Payment Summary (CIS) report
 *   3. ConfirmPayroll modal
 *   4. ConfirmPaymentModal
 *   5. PaymentFileModal
 *
 * Design rules:
 *   1. _otherDeduction can be negative — always apply Math.abs.
 *   2. Self Bill Ref shows raw SFB-xxx ID (unlike PAYE which replaces SFB→PAY).
 *   3. CIS margin is always shown (unlike PAYE which hides for PAYEE model).
 */

import { TypedMessages } from '@nxsys/hooks/useTypedIntl';
import { PayrollCountryType } from '@nxsys/types/models/Common';
import { DraftPayroll } from '@nxsys/types/models/payroll';
import { formatFullName, showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { ColumnsType } from 'antd/es/table';
import { isEmpty } from 'lodash';
import moment from 'moment';

// ---------------------------------------------------------------------------
// Params type
// ---------------------------------------------------------------------------

export interface CISColumnParams {
  messages: TypedMessages;
  payrollCountry: PayrollCountryType;
  isBureau?: boolean;
}

type Col = ColumnsType<DraftPayroll>[number];

function cx(p: CISColumnParams) {
  return showCurrencyByCountry(p.payrollCountry);
}

// ===========================================================================
// INDIVIDUAL COLUMN FUNCTIONS
// ===========================================================================

// ---------------------------------------------------------------------------
// CIS Identity / Reference columns
// ---------------------------------------------------------------------------

export const timeSheetRefColumn = (p: CISColumnParams): Col => ({
  key: 'timeSheetRef',
  title: p.messages['table.timesheetId'],
  dataIndex: 'timeSheetRef',
  sorter: true,
  sortDirections: ['descend', 'ascend'],
  width: 160,
  render: (value) => value?.id || '_',
});

export const batchColumn = (p: CISColumnParams): Col => ({
  key: 'batch',
  title: p.messages['table.batch'],
  dataIndex: 'timeSheetRef',
  render: (value) => value?.batch || '',
});

export const cisSubcontractorIdColumn = (p: CISColumnParams): Col => ({
  key: 'subcontractorId',
  title: p.messages['table.subcontractorId'],
  dataIndex: 'subcontractorRef',
  sorter: true,
  sortDirections: ['descend', 'ascend'],
  render: (value) => value?.id || '_',
  width: 200,
});

export const cisAgencyRefColumn = (p: CISColumnParams): Col => ({
  key: 'agencyRef',
  title: p.isBureau
    ? p.messages['table.employerRef']
    : p.messages['common.agencyRef'],
  dataIndex: 'subcontractorRef',
  width: 250,
  render: (value) =>
    `${value?.departmentPrefix || value?.primaryAgency?.agencyPrefix || '___'}/${
      value?.agencyRef || value?.systemId || '_'
    }`,
});

export const cisAgencyNameColumn = (p: CISColumnParams): Col => ({
  key: 'agencyName',
  title: p.isBureau
    ? p.messages['table.employer']
    : p.messages['common.agencyName'],
  dataIndex: 'agencyName',
  sorter: true,
  width: 350,
  sortDirections: ['descend', 'ascend'],
});

export const cisPaymentCompanyColumn = (p: CISColumnParams): Col => ({
  key: 'paymentCompany',
  title: p.isBureau
    ? p.messages['common.employer']
    : p.messages['common.paymentCompany'],
  dataIndex: 'companyRef',
  width: 350,
  render: (value) => value?.companyName || '_',
});

export const cisNameColumn = (p: CISColumnParams): Col => ({
  key: 'name',
  title: p.messages['common.name'],
  dataIndex: 'subcontractorRef',
  width: 200,
  render: (value) => formatFullName(value) || '_',
});

// ---------------------------------------------------------------------------
// CIS Financial columns
// ---------------------------------------------------------------------------

export const cisTimesheetValueColumn = (p: CISColumnParams): Col => ({
  key: 'timesheetValue',
  title: p.messages['common.timesheetValue'],
  align: 'right',
  dataIndex: 'timesheetValue',
  width: 180,
  render: (value) => formatMoney(value || 0, cx(p)),
});

export const cisTotalHoursColumn = (p: CISColumnParams): Col => ({
  key: 'totalHours',
  title: p.messages['common.hours'],
  align: 'right',
  dataIndex: '_totalHours',
  width: 150,
  render: (value) => value ?? '_',
});

/** CIS margin — always shown (unlike PAYE which hides for PAYEE model). */
export const cisMarginColumn = (p: CISColumnParams): Col => ({
  key: 'margin',
  title: p.messages['common.margin'],
  align: 'right',
  dataIndex: 'margin',
  render: (value) => formatMoney(value || 0, cx(p)),
  sorter: true,
  width: 150,
  sortDirections: ['descend', 'ascend'],
});

export const grossColumn = (p: CISColumnParams): Col => ({
  key: 'gross',
  title: p.messages['common.gross'],
  align: 'right',
  dataIndex: 'gross',
  render: (value) => formatMoney(value || 0, cx(p)),
  sorter: true,
  width: 150,
  sortDirections: ['descend', 'ascend'],
});

export const materialsColumn = (p: CISColumnParams): Col => ({
  key: 'materials',
  title: p.messages['common.materials'],
  align: 'right',
  dataIndex: 'materials',
  render: (value) => formatMoney(value || 0, cx(p)),
  sorter: true,
  width: 150,
  sortDirections: ['descend', 'ascend'],
});

export const vatColumn = (p: CISColumnParams): Col => ({
  key: 'vat',
  title: p.messages['common.vat'],
  align: 'right',
  dataIndex: 'vat',
  render: (value) => formatMoney(value || 0, cx(p)),
  sorter: true,
  sortDirections: ['descend', 'ascend'],
  width: 150,
});

export const cisNetColumn = (p: CISColumnParams): Col => ({
  key: 'net',
  title: p.messages['common.netPay'],
  align: 'right',
  dataIndex: 'net',
  render: (value) => formatMoney(value || 0, cx(p)),
  sorter: true,
  width: 150,
  sortDirections: ['descend', 'ascend'],
});

export const cisStatusColumn = (p: CISColumnParams): Col => ({
  key: 'cisStatus',
  title: p.messages['table.cisStatus'],
  align: 'center',
  dataIndex: 'cisStatus',
  sorter: true,
  sortDirections: ['descend', 'ascend'],
});

export const cisDeductionColumn = (p: CISColumnParams): Col => ({
  key: 'cisDeduction',
  title: p.messages['common.cisDeduction'],
  align: 'right',
  dataIndex: 'cisDeduction',
  width: 250,
  render: (value) => formatMoney(value || 0, cx(p)),
  sorter: true,
  sortDirections: ['descend', 'ascend'],
});

// ---------------------------------------------------------------------------
// Common columns (shared with PAYE but defined here for CIS context)
// ---------------------------------------------------------------------------

export const cisTaxPeriodColumn = (p: CISColumnParams): Col => ({
  key: 'taxPeriod',
  title: p.messages['common.taxPeriod'],
  align: 'right',
  render: (record) =>
    `${record?.taxPeriod || ''}/${
      record?.taxYear || record?.selfBillRef?.taxYear || ''
    }`,
  sorter: true,
  width: 160,
  sortDirections: ['descend', 'ascend'],
});

export const cisOtherPaymentColumn = (p: CISColumnParams): Col => ({
  key: 'otherPayment',
  title: p.messages['common.otherPayment'],
  align: 'right',
  dataIndex: '_otherPayment',
  render: (value) => formatMoney(value || 0, cx(p)),
  sorter: true,
  sortDirections: ['descend', 'ascend'],
  width: 200,
});

/** _otherDeduction can be negative — always display absolute value. */
export const cisOtherDeductionColumn = (p: CISColumnParams): Col => ({
  key: 'otherDeduction',
  title: p.messages['common.otherDeduction'],
  align: 'right',
  dataIndex: '_otherDeduction',
  render: (value) => formatMoney(Math.abs(value) || 0, cx(p)),
  sorter: true,
  sortDirections: ['descend', 'ascend'],
  width: 200,
});

export const cisPaymentDateColumn = (p: CISColumnParams): Col => ({
  key: 'paymentDate',
  title: p.messages['common.paymentDate'],
  align: 'right',
  dataIndex: 'paymentDate',
  sorter: true,
  sortDirections: ['descend', 'ascend'],
  render: (value) =>
    value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '',
  width: 200,
});

// ---------------------------------------------------------------------------
// Self-Bill / Invoice columns
// ---------------------------------------------------------------------------

export const selfBillRefColumn = (p: CISColumnParams): Col => ({
  key: 'selfBillRef',
  title: p.messages['table.selfBillRef'],
  dataIndex: 'selfBillRef',
  render: (value) => value?.id || '_',
  width: 200,
});

export const selfBillDateColumn = (p: CISColumnParams): Col => ({
  key: 'selfBillDate',
  title: p.messages['table.selfBillDate'],
  align: 'right',
  dataIndex: ['selfBillRef', 'billDate'],
  sorter: true,
  sortDirections: ['descend', 'ascend'],
  render: (value) =>
    value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '',
  width: 200,
});

/** Simple invoice number column (for draft CIS). */
export const invoiceNoColumn = (p: CISColumnParams): Col => ({
  key: 'invoiceNo',
  title: p.messages['timesheet.salesInvRef'],
  dataIndex: 'invoiceNo',
  sorter: true,
  sortDirections: ['descend', 'ascend'],
  width: 200,
});

/** Complex invoice number column (for confirmed CIS — handles multi-invoice). */
export const invoiceNoConfirmedColumn = (p: CISColumnParams): Col => ({
  key: 'invoiceNo',
  title: p.messages['timesheet.salesInvRef'],
  sorter: true,
  sortDirections: ['descend', 'ascend'],
  width: 200,
  render: (record) => {
    const { timeSheetRef, invoiceNos, invoiceNo } = record;
    if (timeSheetRef || isEmpty(invoiceNos)) return invoiceNo || '';
    else if (invoiceNos && invoiceNos.length === 1) return invoiceNos[0];
    return p.messages['common.multi'] || 'Multi';
  },
});

// ===========================================================================
// CONVENIENCE GROUP FUNCTIONS
// ===========================================================================

/** CIS identity columns: timesheet ref, batch, subcontractor, agency, company, name */
export const getCISIdentityColumns = (p: CISColumnParams): ColumnsType<DraftPayroll> => [
  timeSheetRefColumn(p),
  batchColumn(p),
  cisSubcontractorIdColumn(p),
  cisAgencyRefColumn(p),
  cisAgencyNameColumn(p),
  cisPaymentCompanyColumn(p),
  cisNameColumn(p),
  // Tax Period sits right after the name column so it stays visible without
  // horizontal scrolling, matching the legacy report layout.
  cisTaxPeriodColumn(p),
];

/** CIS financial columns: timesheetValue, margin, gross, materials, vat, net */
export const getCISFinancialColumns = (p: CISColumnParams): ColumnsType<DraftPayroll> => [
  cisTimesheetValueColumn(p),
  cisTotalHoursColumn(p),
  cisMarginColumn(p),
  grossColumn(p),
  materialsColumn(p),
  vatColumn(p),
  cisNetColumn(p),
];

/** CIS deduction columns: cisStatus, cisDeduction */
export const getCISDeductionColumns = (p: CISColumnParams): ColumnsType<DraftPayroll> => [
  cisStatusColumn(p),
  cisDeductionColumn(p),
];

/** CIS common columns: otherPayment, otherDeduction, paymentDate */
export const getCISCommonColumns = (p: CISColumnParams): ColumnsType<DraftPayroll> => [
  cisOtherPaymentColumn(p),
  cisOtherDeductionColumn(p),
  cisPaymentDateColumn(p),
];

/** Self-bill columns: selfBillRef, selfBillDate, invoiceNo */
export const getSelfBillColumns = (p: CISColumnParams): ColumnsType<DraftPayroll> => [
  selfBillRefColumn(p),
  selfBillDateColumn(p),
  invoiceNoColumn(p),
];

// ===========================================================================
// MASTER SET — all CIS shared columns (excludes actions)
// ===========================================================================

/**
 * Returns ALL shared CIS columns in standard order (20 columns).
 * Does NOT include the actions column (unique per page).
 *
 * Usage:
 *   const cols = [actionsCol, ...getAllCISColumns(p)];
 */
export const getAllCISColumns = (p: CISColumnParams): ColumnsType<DraftPayroll> => [
  ...getCISIdentityColumns(p),
  ...getCISFinancialColumns(p),
  ...getCISDeductionColumns(p),
  ...getCISCommonColumns(p),
  ...getSelfBillColumns(p),
];
